import { useState, useEffect } from "react";
import { Link, NavLink } from "react-router-dom";
import { Menu, X, Phone } from "lucide-react";
import { BUSINESS, ASSETS } from "@/data";

const LINKS = [
  { label: "HOME", to: "/" },
  { label: "ABOUT US", to: "/about" },
  { label: "SERVICES", to: "/services" },
  { label: "PROJECTS", to: "/projects" },
  { label: "CONTACT", to: "/contact" },
];

export default function Navbar() {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 30);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <header
      data-testid="main-navbar"
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        scrolled ? "bg-charcoal/95 backdrop-blur shadow-lg" : "bg-charcoal"
      }`}
    >
      <div className="max-w-7xl mx-auto px-5 lg:px-8">
        <div className="flex items-center justify-between h-20 lg:h-24">
          {/* Logo */}
          <Link to="/" data-testid="navbar-logo" className="flex items-center gap-3">
            <img src={ASSETS.logo} alt="L3 Construction" className="h-12 w-12 lg:h-16 lg:w-16 object-contain" />
            <div className="leading-none">
              <div className="font-display text-xl lg:text-3xl tracking-wide" style={{ color: "var(--l3-cream)" }}>
                L3 CONSTRUCTION
              </div>
              <div className="font-cond text-[10px] lg:text-xs tracking-[0.35em] text-gold">
                {BUSINESS.tagline.toUpperCase()}
              </div>
            </div>
          </Link>

          {/* Desktop links */}
          <nav className="hidden lg:flex items-center gap-9 font-cond text-sm tracking-widest" style={{ color: "var(--l3-cream)" }}>
            {LINKS.map((l) => (
              <NavLink
                key={l.label}
                to={l.to}
                data-testid={`nav-link-${l.label.toLowerCase().replace(/\s+/g, "-")}`}
                className={({ isActive }) => `nav-link ${isActive ? "active text-gold" : ""}`}
              >
                {l.label}
              </NavLink>
            ))}
          </nav>

          {/* CTA */}
          <Link
            to="/contact"
            data-testid="navbar-quote-btn"
            className="hidden lg:inline-flex items-center font-cond tracking-widest text-sm px-7 py-3 btn-outline-gold rounded-sm"
          >
            GET A QUOTE
          </Link>

          {/* Mobile toggle */}
          <button
            data-testid="mobile-menu-toggle"
            onClick={() => setOpen(!open)}
            className="lg:hidden"
            style={{ color: "var(--l3-cream)" }}
            aria-label="Toggle menu"
          >
            {open ? <X size={28} /> : <Menu size={28} />}
          </button>
        </div>
      </div>

      {/* Mobile menu */}
      {open && (
        <div data-testid="mobile-menu" className="lg:hidden bg-wood-dark border-t border-white/10">
          <nav className="flex flex-col px-6 py-4 font-cond tracking-widest" style={{ color: "var(--l3-cream)" }}>
            {LINKS.map((l) => (
              <NavLink
                key={l.label}
                to={l.to}
                onClick={() => setOpen(false)}
                data-testid={`mobile-nav-${l.label.toLowerCase().replace(/\s+/g, "-")}`}
                className={({ isActive }) => `py-3 border-b border-white/10 transition-colors ${isActive ? "text-gold" : "hover:text-gold"}`}
              >
                {l.label}
              </NavLink>
            ))}
            <a href={BUSINESS.phoneHref} className="flex items-center gap-2 py-4 text-gold">
              <Phone size={18} /> {BUSINESS.phone}
            </a>
          </nav>
        </div>
      )}
    </header>
  );
}
